import React, { useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef, ForwardRefExoticComponent, PropsWithoutRef, RefAttributes } from 'react';
import styles from './index.module.less';
import { Graph, Cell, Node, Path } from '@antv/x6';
import Hierarchy from '@antv/hierarchy';
import { MindMapData, HierarchyResult, SelectTreeProps } from './type';
import { cloneDeep } from 'lodash';
import * as uuid from 'uuid';
import { getChildIds } from '../../utils';
import { Checkbox, Tooltip  } from 'antd';
import '@antv/x6-react-shape';
import './registerList';

const SelectTree: ForwardRefExoticComponent<PropsWithoutRef<SelectTreeProps> & RefAttributes<Record<any, any>>> = forwardRef((
  {
    onSelectNodeChange,
    showTreeData,
  },
  ref,
) => {
  const containerId = uuid.v4();

  const activeNodeRef = useRef<any>();
  const relationGraphRef = useRef<Graph>();

  const selectNode = (nodeData: any, isSelected: boolean) => {
    const allNode = relationGraphRef.current.getNodes();
    activeNodeRef.current = isSelected ? undefined : nodeData;
    onSelectNodeChange && onSelectNodeChange(nodeData);
    allNode.forEach((el: any) => {
      if (el.id === nodeData.id) {
        el.prop('selected', !isSelected);
      } else {
        el.prop('selected', false);
      }
    });
  };
  const collapseChange = (node: Node) => {
    const collapsed = node.prop('collapsed');
    const nodeId = node.id;
    const idList = getChildIds([showTreeData], nodeId);
    const allNode = relationGraphRef.current.getNodes();
    allNode.forEach((el: any) => {
      if (idList.includes(el.id)) {
        collapsed ? el.show() : el.hide();
        el.prop('collapsed', !collapsed);
      }
    });
    node.prop('collapsed', !collapsed);
  };
  const initRender = () => {
    relationGraphRef.current?.resetCells([]);
    relationGraphRef.current = new Graph({
      container: document.getElementById(`container${containerId}`)!,
      width: 520,
      height: 600,
      panning: true,
      // autoResize: true,
      background: { color: 'rgb(251, 251, 251)' },
      selecting: {
        enabled: true,
      },
      interacting: false,
    });
    render(showTreeData);
  };
  const render = (renderData: any) => {
    const result: HierarchyResult = Hierarchy.mindmap(renderData, {
      direction: 'H',
      getHeight() {
        return 40;
      },
      getWidth() {
        return 110;
      },
      getHGap() {
        return 40;
      },
      getVGap() {
        return 20;
      },
      getSide: () => {
        return 'right';
      },
    });
    const cells: Cell[] = [];
    const traverse = (hierarchyItem: HierarchyResult) => {
      if (hierarchyItem) {
        const { data, children } = hierarchyItem;
        cells.push(
          relationGraphRef.current.createNode({
            id: data.id,
            x: hierarchyItem.x,
            y: hierarchyItem.y,
            width: 110,
            height: 40,
            label: data.label,
            type: data.type,
            shape: 'react-shape',
            component(node: Node) {
              const selected = node.prop<string>('selected');
              const collapsed = node.prop('collapsed');
              const hasChildren = data.children && data.children.length > 0;
              const childNum = data.children ? data.children.length : 0;
              return (
                <div
                  className={`${styles.custom_box} ${selected ? styles.custom_box_selected : ''}`}
                >
                  <div
                    className={styles.left_line}
                    style={data.disabled ? { background: '#ADADAD' } : {}}
                  />
                  <div
                    className={styles.label_box}
                    style={data.disabled ? { color: '#666666' } : { cursor: 'pointer' }}
                    onClick={() => {
                      if (!data.disabled) {
                        selectNode(data, selected);
                      }
                    }}
                  >
                    <Tooltip title={data.label}>
                      {data.label}
                    </Tooltip>
                  </div>
                  {hasChildren && <div className={styles.custom_footer} >
                    <div className={styles.event_icon} onClick={() => collapseChange(node)}>
                      <span className={styles.event_icon_content}>{collapsed ? '+' : '-'}</span>
                    </div>
                    {collapsed && childNum && (
                    <div className={styles.child_num}>
                      <div className={styles.child_num_line} />
                      <div className={styles.child_num_text}>+{childNum}</div>
                    </div>
                    )}
                  </div>}
                </div>
              );
            },
          }),
        );
        if (children) {
          children.forEach((item: HierarchyResult) => {
            const { id, data } = item;
            cells.push(
              relationGraphRef.current.createEdge({
                shape: 'mindmap-edge',
                source: hierarchyItem.id,
                target: id,
              }),
            );
            traverse(item);
          });
        }
      }
    };
    traverse(result);
    relationGraphRef.current.resetCells(cells);
    relationGraphRef.current.centerContent();
    if (activeNodeRef.current) {
      selectNode(activeNodeRef.current, false);
    }
  };

  useEffect(() => {
    initRender();
  }, [showTreeData]);

  const getCheckNodes = () => {
    return showTreeData;
  };

  useImperativeHandle(ref, () => ({
    getCheckNodes,
  }));

  return (
    <div>
      <div id={`container${containerId}`} className={styles.tree_box} />
    </div>
  );
});

export default SelectTree;
